import { ApiReportSectionFieldsFieldTypeChoices } from "@api/graphql/types-and-hooks";
import { ReportFieldAssessmentAppointments } from "@components/ReportField/inputs/ReportFieldAssessmentAppointments";
import { ReportFieldBlankSignatureLine } from "@components/ReportField/inputs/ReportFieldBlankSignatureLine";
import { ReportFieldCheckbox } from "@components/ReportField/inputs/ReportFieldCheckbox";
import { ReportFieldCoordinationOfCare } from "@components/ReportField/inputs/ReportFieldCoordinationOfCare";
import { ReportFieldDate } from "@components/ReportField/inputs/ReportFieldDate";
import { ReportFieldDatetime } from "@components/ReportField/inputs/ReportFieldDatetime";
import { ReportFieldDsmTable } from "@components/ReportField/inputs/ReportFieldDsmTable";
import { ReportFieldImage } from "@components/ReportField/inputs/ReportFieldImage";
import { ReportFieldInput } from "@components/ReportField/inputs/ReportFieldInput";
import { ReportFieldRadio } from "@components/ReportField/inputs/ReportFieldRadio";
import { ReportFieldSelect } from "@components/ReportField/inputs/ReportFieldSelect";
import { ReportFieldSpecifiersTable } from "@components/ReportField/inputs/ReportFieldSpecifiersTable";
import { ReportFieldTable } from "@components/ReportField/inputs/ReportFieldTable";
import { ReportFieldTreatmentOptionTable } from "@components/ReportField/inputs/ReportFieldTreatmentOptionTable";
import { ReportFieldTreatmentPlan } from "@components/ReportField/inputs/ReportFieldTreatmentPlan";
import { ReportFieldTypedSignature } from "@components/ReportField/inputs/ReportFieldTypedSignature";
import { ReportFieldWetSignature } from "@components/ReportField/inputs/ReportFieldWetSignature";
import { ReportFieldWysiwyg } from "@components/ReportField/inputs/ReportFieldWysiwyg";
import { DATE_FIELD_IDENTIFIER_OVERRIDES } from "@components/ReportField/inputs/dateFieldUtils";
import { ReportFieldInputProps, ReportFieldType, StructuralReportFieldProps } from "@components/ReportField/types";

const REPORT_FIELD_INPUTS: Partial<Record<ReportFieldType, React.FC<ReportFieldInputProps>>> = {
  [ApiReportSectionFieldsFieldTypeChoices.SimpleText]: ReportFieldInput,
  [ApiReportSectionFieldsFieldTypeChoices.Date]: ReportFieldDate,
  [ApiReportSectionFieldsFieldTypeChoices.Datetime]: ReportFieldDatetime,
  [ApiReportSectionFieldsFieldTypeChoices.Wysiwyg]: ReportFieldWysiwyg,
  [ApiReportSectionFieldsFieldTypeChoices.Select]: ReportFieldSelect,
  [ApiReportSectionFieldsFieldTypeChoices.Radio]: ReportFieldRadio,
  [ApiReportSectionFieldsFieldTypeChoices.Checkbox]: ReportFieldCheckbox,

  [ApiReportSectionFieldsFieldTypeChoices.Image]: ReportFieldImage,
  [ApiReportSectionFieldsFieldTypeChoices.AssessmentImage]: ReportFieldImage,

  [ApiReportSectionFieldsFieldTypeChoices.BlankSignatureLine]: ReportFieldBlankSignatureLine,
  [ApiReportSectionFieldsFieldTypeChoices.TypedSignature]: ReportFieldTypedSignature,
  // One field type offering all three capture methods (drawn/typed/uploaded)
  // at fill time, replacing the separately-configured UploadedSignature/
  // DrawnSignature field types (unify-wet-signature-field-type).
  [ApiReportSectionFieldsFieldTypeChoices.WetSignature]: ReportFieldWetSignature,

  [ApiReportSectionFieldsFieldTypeChoices.Table]: ReportFieldTable,
  [ApiReportSectionFieldsFieldTypeChoices.AssessmentAppointments]: ReportFieldAssessmentAppointments,
  [ApiReportSectionFieldsFieldTypeChoices.CoordinationOfCare]: ReportFieldCoordinationOfCare,
  [ApiReportSectionFieldsFieldTypeChoices.DsmTable]: ReportFieldDsmTable,
  [ApiReportSectionFieldsFieldTypeChoices.SpecifiersTable]: ReportFieldSpecifiersTable,
  [ApiReportSectionFieldsFieldTypeChoices.TreatmentOptionTable]: ReportFieldTreatmentOptionTable,
};

// Field types that render their own complete UI (no scalar `field.content`,
// no label/autosave/error shell) — see StructuralReportFieldProps.
const STRUCTURAL_REPORT_FIELDS: Partial<Record<ReportFieldType, React.FC<StructuralReportFieldProps>>> = {
  [ApiReportSectionFieldsFieldTypeChoices.TreatmentPlan]: ReportFieldTreatmentPlan,
};

const DATE_OVERRIDE_COMPONENTS: Record<"date" | "datetime", React.FC<ReportFieldInputProps>> = {
  date: ReportFieldDate,
  datetime: ReportFieldDatetime,
};

export const getReportFieldInput = (
  fieldType: ReportFieldType | string,
  fieldIdentifier?: string | null,
): React.FC<ReportFieldInputProps> | undefined => {
  const overrideKind = fieldIdentifier ? DATE_FIELD_IDENTIFIER_OVERRIDES[fieldIdentifier] : undefined;
  return overrideKind ? DATE_OVERRIDE_COMPONENTS[overrideKind] : REPORT_FIELD_INPUTS[fieldType as ReportFieldType];
};

export const getStructuralReportField = (
  fieldType: ReportFieldType | string,
): React.FC<StructuralReportFieldProps> | undefined => STRUCTURAL_REPORT_FIELDS[fieldType as ReportFieldType];
